import { Dispatch, SetStateAction, useState } from "react";
import { TranslatableContent, TranslatableNewsletterContent } from "../components/authoring/models";
import { IEmailTranslatableContent } from "modules/emails/models";
import { IHeaderTranslation } from "modules/contentBands/models";

/**
 * Encapsulate accordion logic for analytics
 * @param {
 *   translations: TranslatedContentType<T>; // T extends TranslatableContentType
 *   defaultContent: TranslatableContentType;
 *   activeLcid: string;
 *   onChangeTranslatedContent: (toSet: TranslatedContentType<T>) => void;
 *   translationHasContent: (lcid: string) => boolean;
 *   onTranslate: (inputLanguage: string) => void;
 *   onSelectLanguage: (key: string) => void;
 *   onRemoveLanguage: (key: string) => void;
 * }
 * @returns {
 *   showTranslationDialog: boolean;
 *   showRemoveLanguageDialog: boolean;
 *   showReplaceContentDialog: boolean;
 *   setRemoveLanguage: Dispatch<SetStateAction<string | undefined>>;
 *   onRemoveTranslation: (item: Record<string, string>) => void;
 *   onSelectTranslation: (item: Record<string, string>) => void;
 *   onShowTranslationDialog: () => void
 *   onCloseTranslationDialog: () => void;
 *   onConfirmDialog: (inputLanguage: string) => void;
 *   onConfirmRemoveLanguage: (lcidToRemove?: string) => void;
 *   onConfirmRemoveLanguageAndTranslate: () => void;
 *   onCloseReplaceContentDialog: () => void;
 *   onCloseRemoveLanguageDialog: () => void;
 */

//Intended to be the [lcid]: {content} objects, but may be different depending on what is calling the hook.
export type TranslatedContentType<T> = Record<string, T>;

export type TranslatableContentType = TranslatableNewsletterContent | TranslatableContent | IEmailTranslatableContent | IHeaderTranslation;

export type TranslatedContentProps<T extends TranslatableContentType> = {
    translations: TranslatedContentType<T>; 
    defaultContent: T;
    activeLcid: string;
    onChangeTranslatedContent: (toSet: TranslatedContentType<T>) => void;
    translationHasContent: (lcid: string) => boolean | undefined;
    doesLcidHaveRequiredContent: (lcid: string) => boolean;
    onTranslate: (inputLanguage: string, outputLanguage?: string) => void;
    onSelectLanguage: (key: string) => void;
    onRemoveLanguage: (key: string) => void;
    defaultLcid: string;
    autoTranslateOnAddLanguage?: boolean;
}

export type TranslatedContentStatus = {
    showTranslationDialog: boolean;
    showReplaceContentDialog: boolean;
    showRemoveLanguageDialog: boolean;
    setRemoveLanguage: Dispatch<SetStateAction<string | undefined>>;
    onRemoveTranslation: (item: Record<string, string>) => void;
    onSelectTranslation: (item: Record<string, string>) => void;
    onShowTranslationDialog: () => void;
    onCloseTranslationDialog: () => void;
    onConfirmTranslationDialog: (inputLanguage: string) => void;
    onConfirmRemoveLanguage: (lcidToRemove?: string) => void;
    onConfirmReplaceContentAndTranslate: () => void;
    onCloseReplaceContentDialog: () => void;
    onCloseRemoveLanguageDialog: () => void;
}

const useTranslatedContent = <T extends TranslatableContentType,>(props: TranslatedContentProps<T>): TranslatedContentStatus => {
    const [showTranslationDialog, setShowTranslationDialog] = useState(false);
    const [showReplaceContentDialog, setShowReplaceContentDialog] = useState(false);
    const [showRemoveLanguageDialog, setShowRemoveLanguageDialog] = useState(false);
    const [removeLanguage, setRemoveLanguage] = useState<string | undefined>();
    const [inputLanguage, setInputLanguage] = useState<string | undefined>();
    const autoTranslateEnabled = props.autoTranslateOnAddLanguage || false;

    /**
     * Check if a translation has content
     * - if it does, show confirm dialog
     * - otherwise, continue remove
     */
    const onRemoveTranslation = (item: Record<string, string>) => {
        if (props.translationHasContent(item.key)) {
            setRemoveLanguage(item.key);
            setShowRemoveLanguageDialog(true);
        } 
        else {
            onConfirmRemoveLanguage(item.key);
        }
    };

    /**
     * Select a translation
     * - if translation does not exist, add it
     */
    const onSelectTranslation = (item: Record<string, string>) => {
        // add to translations
        let newTranslations = { ...props.translations };
        
        if (!Object.keys(newTranslations).includes(item.key)) {
            newTranslations[item.key] = props.defaultContent;
            props.onChangeTranslatedContent(newTranslations);
            
            //Since the language is brand new, we add translated content
            //Only do this if default lcid has body and title
            if(autoTranslateEnabled && props.doesLcidHaveRequiredContent(props.defaultLcid)) {
                props.onTranslate(props.defaultLcid, item.key);
            }
        }

        props.onSelectLanguage(item.key);
    };

    const removeTranslation = (lcid: string) => {
        // remove from translations
        let newTranslations = { ...props.translations };
        if (Object.keys(newTranslations).includes(lcid)) {
            delete newTranslations[lcid];
            props.onChangeTranslatedContent(newTranslations);
        }
    };

    /**
     * Handle showing the translation dialog
     */
    const onShowTranslationDialog = (): void => {
        setShowTranslationDialog(true);
    };

    /**
     * Handle closing the translation dialog
     */
    const onCloseTranslationDialog = () => {
        setShowTranslationDialog(false);
    };

    /**
     * Handle dialog selection for the translation dialog
     */
    const onConfirmTranslationDialog = (inputLanguage: string) => {
        setShowTranslationDialog(false);

        if (props.translationHasContent(props.activeLcid)) {
            setInputLanguage(inputLanguage);
            setShowReplaceContentDialog(true);
        }
        else {
            props.onTranslate(inputLanguage);
        }
    };

    /**
     * Handle confirm remove language or remove language with no content
     * @param lcidToRemove - used when no content (no confirm dialog)
     */
    const onConfirmRemoveLanguage = (lcidToRemove?: string) => {
        lcidToRemove = lcidToRemove || removeLanguage;

        if (lcidToRemove) {
            props.onRemoveLanguage(lcidToRemove); // prop function
            removeTranslation(lcidToRemove); // internal function
            setRemoveLanguage(undefined);
        }

        setShowRemoveLanguageDialog(false);
    }

    const onConfirmReplaceContentAndTranslate = () => {
        props.onTranslate(inputLanguage || "");
        setInputLanguage(undefined);
        onCloseReplaceContentDialog();
    }

    const onCloseReplaceContentDialog = () => {
        setInputLanguage(undefined);
        setShowReplaceContentDialog(false);
    }

    const onCloseRemoveLanguageDialog = () => {
        setRemoveLanguage(undefined);
        setShowRemoveLanguageDialog(false);
    }

    return {
        showTranslationDialog,
        showReplaceContentDialog,
        showRemoveLanguageDialog,
        setRemoveLanguage,
        onRemoveTranslation,
        onSelectTranslation,
        onShowTranslationDialog,
        onCloseTranslationDialog,
        onConfirmTranslationDialog,
        onConfirmRemoveLanguage,
        onConfirmReplaceContentAndTranslate,
        onCloseReplaceContentDialog,
        onCloseRemoveLanguageDialog
    }
}

export default useTranslatedContent;
